import React, { useState, useRef, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../Context/AuthContext'
import { Avatar, useConfirm } from './AdminUI'

function UserMenu({ label }) {
  const navigate = useNavigate()
  const { user, isAuthenticated, logout } = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  const [confirm, confirmDialog] = useConfirm()
  const menuRef = useRef(null)
  const timeoutRef = useRef(null)

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setIsOpen(true)
  }

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false)
    }, 200) // Small delay to allow moving to dropdown
  }

  const handleLogout = () => {
    setIsOpen(false)
    confirm({
      tone: 'info',
      icon: 'fa-arrow-right-from-bracket',
      title: 'Log out of your account?',
      message: 'Your cart and wishlist on this device will be cleared. You can sign back in any time.',
      item: user
        ? {
            media: <Avatar name={user.name || user.email} size="lg" />,
            title: user.name || 'My account',
            meta: user.email,
          }
        : null,
      confirmLabel: 'Log out',
      cancelLabel: 'Stay signed in',
      onConfirm: () => {
        logout()
        navigate('/')
      },
    })
  }

  return (
    <>
      <style>{`
        .user-menu-button {
          position: relative;
          cursor: pointer;
          padding: 0;
          overflow: visible;
        }

        .user-menu-button.active {
          border-color: #2366b1 !important;
          background: rgba(52, 144, 206, 0.1) !important;
          box-shadow: 0 0 0 3px rgba(52, 144, 206, 0.16) !important;
        }

        .user-icon {
          color: inherit !important;
          font-size: 16px !important;
          line-height: 1;
          transition: none;
        }

        .user-menu-button:hover .user-icon {
          color: inherit !important;
          transform: none;
        }
        
        .user-menu-dropdown {
          position: absolute;
          top: calc(100% + 10px);
          right: 0;
          background: #ffffff !important;
          border-radius: 16px;
          box-shadow: 
            0 20px 60px rgba(0, 0, 0, 0.15),
            0 0 0 1px #e2e8f0,
            0 8px 24px rgba(0, 0, 0, 0.1);
          min-width: 240px;
          width: max-content;
          z-index: 10000;
          overflow: hidden;
          border: 1px solid #e2e8f0;
          animation: menuSlideDown 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          opacity: 1;
        }
        
        @keyframes menuSlideDown {
          from {
            opacity: 0;
            transform: translateY(-8px) scale(0.96);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        .user-info-header {
          padding: 18px 20px;
          border-bottom: 1px solid #e2e8f0;
          background: #ffffff !important;
        }
        
        .user-info-avatar {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: #2366b1 !important;
          background-image: none !important;
          color: #ffffff !important;
          -webkit-text-fill-color: #ffffff !important;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 15px;
          font-weight: 700;
          letter-spacing: 0.5px;
          flex-shrink: 0;
          box-shadow: none;
        }
        
        .user-name {
          font-size: 15px;
          font-weight: 700;
          color: #0f172a;
          line-height: 1.4;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          letter-spacing: -0.01em;
        }
        
        .user-email {
          font-size: 12px;
          color: #475569;
          margin-top: 3px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        .menu-items-container {
          padding: 6px 0;
          background: #ffffff !important;
        }
        
        .menu-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 11px 18px;
          color: #1e293b;
          text-decoration: none;
          font-size: 14px;
          font-weight: 600;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          border: none;
          background: none;
          width: 100%;
          text-align: left;
          cursor: pointer;
        }
        
        .menu-item i {
          width: 20px;
          text-align: center;
          font-size: 15px;
          color: #475569;
          transition: all 0.2s ease;
        }
        
        .menu-item:hover {
          background: rgba(52, 144, 206, 0.1);
          color: #2366b1;
        }
        
        .menu-item:hover i {
          color: #2366b1;
          transform: translateX(2px);
        }
        
        .menu-item.logout {
          color: #dc2626;
        }
        
        .menu-item.logout i {
          color: #dc2626;
        }
        
        .menu-item.logout:hover {
          background: rgba(220, 38, 38, 0.1);
          color: #b91c1c;
        }
        
        .menu-item.logout:hover i {
          color: #b91c1c;
        }
        
        .menu-divider {
          height: 1px;
          background: #e2e8f0;
          margin: 8px 0;
        }
        
        @media (max-width: 768px) {
          .user-menu-dropdown {
            right: -10px;
            min-width: 220px;
          }
        }
      `}</style>
      
      <div 
        className="user-menu" 
        ref={menuRef} 
        style={{ position: 'relative' }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`fab-header-action user-menu-button ${isOpen ? 'active' : ''}`}
          title={isAuthenticated ? (user?.name || 'User') : 'Account'}
          aria-label={isAuthenticated ? 'Account menu' : 'Account'}
          aria-expanded={isOpen}
        >
          <i className="fa-solid fa-user user-icon" aria-hidden="true" />
          {label ? (
            <span className="fab-header-action__label">
              {isAuthenticated ? (user?.name || '').split(' ')[0] || label : label}
            </span>
          ) : null}
        </button>

      {isOpen && (
        <div
          className="user-menu-dropdown"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{ background: '#ffffff', opacity: 1 }}
        >
          {isAuthenticated ? (
            <>
              {/* User Info Header */}
              <div className="user-info-header">
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '14px'
                }}>
                  <div className="user-info-avatar">
                    <i className="fa-solid fa-user" aria-hidden="true"></i>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="user-name">
                      {user?.name || 'User'}
                    </div>
                    <div className="user-email">
                      {user?.email || ''}
                    </div>
                  </div>
                </div>
              </div>

              {/* Menu Items */}
              <div className="menu-items-container">
                {/* Dashboard - Only for admins */}
                {user?.role === 'admin' && (
                  <Link
                    to="/dashboard"
                    onClick={() => setIsOpen(false)}
                    className="menu-item"
                  >
                    <i className="fa-solid fa-gauge-high"></i>
                    <span>Dashboard</span>
                  </Link>
                )}

                <Link
                  to="/orders"
                  onClick={() => setIsOpen(false)}
                  className="menu-item"
                >
                  <i className="fa-solid fa-box"></i>
                  <span>My Orders</span>
                </Link>

                <Link
                  to="/wishlist"
                  onClick={() => setIsOpen(false)}
                  className="menu-item"
                >
                  <i className="fa-solid fa-heart"></i>
                  <span>Wishlist</span>
                </Link>

                {user?.role === 'admin' && (
                  <Link
                    to="/admin/products"
                    onClick={() => setIsOpen(false)}
                    className="menu-item"
                  >
                    <i className="fa-solid fa-cog"></i>
                    <span>Admin Panel</span>
                  </Link>
                )}
              </div>

              {/* Logout */}
              <div className="menu-divider"></div>
              <div className="menu-items-container">
                <button
                  onClick={handleLogout}
                  className="menu-item logout"
                >
                  <i className="fa-solid fa-right-from-bracket"></i>
                  <span>Logout</span>
                </button>
              </div>
            </>
          ) : (
            /* Not Authenticated - Show Login and Sign Up */
            <div className="menu-items-container">
              <Link
                to="/login"
                onClick={() => setIsOpen(false)}
                className="menu-item"
              >
                <i className="fa-solid fa-right-to-bracket"></i>
                <span>Login</span>
              </Link>

              <Link
                to="/signup"
                onClick={() => setIsOpen(false)}
                className="menu-item"
              >
                <i className="fa-solid fa-user-plus"></i>
                <span>Sign Up</span>
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
    {confirmDialog}
    </>
  )
}

export default UserMenu

